import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getPaymentConfig } from "@/lib/api";
import { formatAmount } from "@/lib/money";
import { applyFavicon, applyTitle } from "@/lib/page-chrome";

/** Mirrors GBP_LOCKED_HOSTS on the server. */
const GBP_LOCKED_HOSTS = new Set([
  'howmanypeoplepaidapoundtoseehowmanypeoplepaidapound.com',
]);

/**
 * The server is the authority on price, but it costs a round trip. Guessing
 * from the hostname first means the pound domain never flashes the wrong
 * symbol, and everywhere else opens on the same default the server will pick
 * for most visitors.
 */
function guessCurrency() {
  if (typeof window === 'undefined') return 'usd';
  const host = window.location.hostname.toLowerCase().replace(/^www\./, '');
  return GBP_LOCKED_HOSTS.has(host) ? 'gbp' : 'usd';
}

/**
 * Shared by the copy, the tab and the pay button. react-query dedupes on the
 * key, so every caller hits one request.
 */
export function usePaymentConfig() {
  const query = useQuery({
    queryKey: ['payment-config'],
    queryFn: getPaymentConfig,
    staleTime: Infinity,
  });

  const currency = query.data?.currency ?? guessCurrency();
  const amountLabel = formatAmount(query.data?.amount ?? 100, currency);

  useEffect(() => {
    applyFavicon(currency);
    applyTitle(amountLabel);
  }, [currency, amountLabel]);

  return { ...query, currency, amountLabel };
}
